import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import {
  useProgressIllusion,
  PsychologicalProgressBar,
} from "@/components/ui/psychological-progress-bar";

const ROTATING_MESSAGE_KEYS = [
  "aiGeneratingOverlay.default.workingOnMaterial",
  "aiGeneratingOverlay.default.thinkingDeeply",
  "aiGeneratingOverlay.default.almostReady",
  "aiGeneratingOverlay.default.polishingDetails",
  "aiGeneratingOverlay.default.creativityInProgress",
  "aiGeneratingOverlay.default.brewingCoffee",
  "aiGeneratingOverlay.default.analyzingOptions",
  "aiGeneratingOverlay.default.turningIdeaIntoContent",
  "aiGeneratingOverlay.default.goodThingsTakeTime",
  "aiGeneratingOverlay.default.workingAtFullSpeed",
  "aiGeneratingOverlay.default.hypeFireInTheShed",
  "aiGeneratingOverlay.default.hypeWhatAFire",
  "aiGeneratingOverlay.default.hypeLovingThisIdea",
  "aiGeneratingOverlay.default.hypeFireComing",
] as const;

const MODULE_MESSAGE_KEYS: Readonly<Record<string, readonly string[]>> = {
  "reel-creator": [
    "aiGeneratingOverlay.modules.reelCreator.creatingScript",
    "aiGeneratingOverlay.modules.reelCreator.analyzingTrends",
    "aiGeneratingOverlay.modules.reelCreator.choosingFormat",
    "aiGeneratingOverlay.modules.reelCreator.findingViralAngle",
  ],
  "ad-creator": [
    "aiGeneratingOverlay.modules.adCreator.writingCopy",
    "aiGeneratingOverlay.modules.adCreator.analyzingAudience",
    "aiGeneratingOverlay.modules.adCreator.creatingVariants",
    "aiGeneratingOverlay.modules.adCreator.choosingCta",
  ],
  "offer-builder": [
    "aiGeneratingOverlay.modules.offerBuilder.buildingOfferSheet",
    "aiGeneratingOverlay.modules.offerBuilder.analyzingUsp",
    "aiGeneratingOverlay.modules.offerBuilder.creatingOfferStructure",
    "aiGeneratingOverlay.modules.offerBuilder.choosingSalesArguments",
  ],
  "vsl-creator": [
    "aiGeneratingOverlay.modules.vslCreator.writingScript",
    "aiGeneratingOverlay.modules.vslCreator.buildingPersuasion",
    "aiGeneratingOverlay.modules.vslCreator.creatingHooks",
    "aiGeneratingOverlay.modules.vslCreator.choosingSocialProof",
  ],
  "youtube-mode": [
    "aiGeneratingOverlay.modules.youtubeMode.creatingOutline",
    "aiGeneratingOverlay.modules.youtubeMode.analyzingSeo",
    "aiGeneratingOverlay.modules.youtubeMode.buildingScriptStructure",
    "aiGeneratingOverlay.modules.youtubeMode.findingHook",
  ],
  "campaign-creator": [
    "aiGeneratingOverlay.modules.campaignCreator.configuringCampaign",
    "aiGeneratingOverlay.modules.campaignCreator.analyzingSettings",
    "aiGeneratingOverlay.modules.campaignCreator.choosingTargeting",
    "aiGeneratingOverlay.modules.campaignCreator.preparingStructure",
  ],
  "carousel-creator": [
    "aiGeneratingOverlay.modules.carouselCreator.designingSlides",
    "aiGeneratingOverlay.modules.carouselCreator.choosingLayout",
    "aiGeneratingOverlay.modules.carouselCreator.creatingStoryFlow",
  ],
  "content-creator": [
    "aiGeneratingOverlay.modules.contentCreator.creatingContent",
    "aiGeneratingOverlay.modules.contentCreator.choosingTone",
    "aiGeneratingOverlay.modules.contentCreator.polishingCopy",
  ],
  "email-creator": [
    "aiGeneratingOverlay.modules.emailCreator.writingEmail",
    "aiGeneratingOverlay.modules.emailCreator.choosingSubject",
    "aiGeneratingOverlay.modules.emailCreator.creatingSequence",
  ],
  "marketing-analyst": [
    "aiGeneratingOverlay.modules.marketingAnalyst.analyzingData",
    "aiGeneratingOverlay.modules.marketingAnalyst.processingMetrics",
    "aiGeneratingOverlay.modules.marketingAnalyst.findingOptimizations",
    "aiGeneratingOverlay.modules.marketingAnalyst.preparingRecommendations",
  ],
} as const;

const ROTATION_INTERVAL_MS = 3500;

interface AiGeneratingOverlayProps {
  readonly message?: string;
  readonly showProgress?: boolean;
  readonly compact?: boolean;
}

export function AiGeneratingOverlay({
  message,
  showProgress = false,
  compact = false,
}: AiGeneratingOverlayProps) {
  const { t } = useTranslation();
  const [messageIndex, setMessageIndex] = useState(() =>
    Math.floor(Math.random() * ROTATING_MESSAGE_KEYS.length)
  );

  useEffect(() => {
    if (message) return;

    const timer = setInterval(() => {
      setMessageIndex((prev) => (prev + 1) % ROTATING_MESSAGE_KEYS.length);
    }, ROTATION_INTERVAL_MS);

    return () => clearInterval(timer);
  }, [message]);

  const displayMessage = message ?? t(ROTATING_MESSAGE_KEYS[messageIndex]);
  const progress = useProgressIllusion({ active: showProgress, tau: 10 });

  if (compact) {
    return (
      <div className="flex items-center gap-2 py-2">
        <Loader2 size={14} className="animate-spin text-purple-400 flex-shrink-0" />
        <span className="text-xs text-white/50 animate-pulse">{displayMessage}</span>
      </div>
    );
  }

  return (
    <div className="p-4 bg-white/[0.03] border border-white/[0.08] rounded-xl">
      <div className="flex items-center gap-3 mb-2">
        <div className="relative flex-shrink-0">
          <Loader2 className="w-5 h-5 animate-spin text-purple-400" />
          <div className="absolute inset-0 w-5 h-5 rounded-full bg-purple-400/20 animate-ping" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-white truncate">{displayMessage}</p>
            {showProgress && (
              <span className="text-[10px] text-white/30 tabular-nums ml-2 shrink-0">
                {Math.round(progress * 100)}%
              </span>
            )}
          </div>
        </div>
      </div>
      {showProgress && (
        <PsychologicalProgressBar
          progress={progress}
          color="purple"
          heightClass="h-1.5"
          shimmer
          className="mt-1"
        />
      )}
    </div>
  );
}

export function useRotatingMessage(moduleId?: string, isActive = true): string {
  const { t } = useTranslation();
  const pool =
    moduleId && MODULE_MESSAGE_KEYS[moduleId]
      ? [...MODULE_MESSAGE_KEYS[moduleId], ...ROTATING_MESSAGE_KEYS]
      : [...ROTATING_MESSAGE_KEYS];

  const [index, setIndex] = useState(() => Math.floor(Math.random() * pool.length));

  useEffect(() => {
    if (!isActive) return;
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % pool.length);
    }, ROTATION_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [isActive, pool.length]);

  return t(pool[index % pool.length]);
}
